import { useEffect, useState, useCallback } from 'react';
import {
  MdDashboard, MdRefresh, MdTrendingUp, MdPeople, MdWork,
  MdCheckCircle, MdAccessTime, MdBarChart, MdPieChart, MdBusinessCenter,
  MdPerson, MdCategory, MdStar, MdStarBorder, MdFileDownload
} from 'react-icons/md';
import PageHeader from '../components/PageHeader';
import { jobDashboardApi } from '../../../api/jobManagementApi';
import { toast } from 'react-toastify';
import '../Admin.css';
import './JobDashboard.css';

const DASHBOARD_TABS = [
  { id: 'overview', label: 'Overview', icon: MdDashboard },
  { id: 'segment', label: 'Segment Wise', icon: MdCategory },
  { id: 'project', label: 'Project Wise', icon: MdBusinessCenter },
  { id: 'recruiter', label: 'Recruiter Performance', icon: MdPerson },
  { id: 'tat', label: 'TAT Analysis', icon: MdAccessTime },
  { id: 'source', label: 'Source Mix', icon: MdPieChart }
];

const SOURCE_COLORS = [
  '#7c3aed', '#2563eb', '#16a34a', '#d97706', '#dc2626', '#0d9488', '#ec4899', '#6366f1'
];

function JobDashboard() {
  const [activeTab, setActiveTab] = useState('overview');
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  // Date filters
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });

  // Dashboard data
  const [summaryStats, setSummaryStats] = useState({
    totalOpenJRs: 0,
    totalCandidates: 0,
    joineesThisMonth: 0,
    avgTAT: 0,
    totalRecruiters: 0,
    fillRate: 0
  });

  const [segmentData, setSegmentData] = useState([]);
  const [projectData, setProjectData] = useState([]);
  const [recruiterData, setRecruiterData] = useState([]);
  const [tatData, setTatData] = useState([]);
  const [sourceData, setSourceData] = useState([]);

  // Load summary stats (Overview)
  const loadSummaryStats = async () => {
    try {
      const response = await jobDashboardApi.getOverview(dateRange.startDate, dateRange.endDate);
      if (response.success && response.data) {
        const data = response.data;
        setSummaryStats({
          totalOpenJRs: data.openRequisitions || 0,
          totalCandidates: data.pipelineCount || 0,
          joineesThisMonth: data.joinersCount || 0,
          avgTAT: data.averageTAT || 0,
          totalRecruiters: data.totalRecruiters || 0,
          fillRate: data.fillRate || 0
        });
      }
    } catch (e) {
      console.error('Failed to load summary stats:', e);
      toast.error('Failed to load overview data');
    }
  };

  // Load segment-wise data
  const loadSegmentData = async () => {
    try {
      const response = await jobDashboardApi.getSegments(dateRange.startDate, dateRange.endDate);
      if (response.success && response.data) {
        const segments = response.data.map((s, idx) => ({
          id: s.id || idx,
          name: s.name || 'Unknown',
          openJRs: s.openJRs || 0,
          pipeline: s.pipeline || 0,
          joined: s.joined || s.filled || 0,
          target: s.target || s.openPositions || 0
        }));
        setSegmentData(segments);
      }
    } catch (e) {
      console.error('Failed to load segment data:', e);
      toast.error('Failed to load segment data');
    }
  };

  // Load project-wise data
  const loadProjectData = async () => {
    try {
      const response = await jobDashboardApi.getProjects(dateRange.startDate, dateRange.endDate);
      if (response.success && response.data) {
        const projects = response.data.map((p, idx) => ({
          id: p.id || idx,
          name: p.name || 'Unknown',
          openJRs: p.openJRs || 0,
          filled: p.filled || 0,
          pending: p.pending || 0
        }));
        setProjectData(projects);
      }
    } catch (e) {
      console.error('Failed to load project data:', e);
      toast.error('Failed to load project data');
    }
  };

  // Load recruiter performance data
  const loadRecruiterData = async () => {
    try {
      const response = await jobDashboardApi.getRecruiters(dateRange.startDate, dateRange.endDate);
      if (response.success && response.data) {
        const recruiters = response.data.map((r, idx) => ({
          id: r.id || idx,
          name: r.name || 'Unknown',
          role: r.role || '',
          activeJRs: r.activeJRs || 0,
          pipeline: r.pipeline || r.totalCandidates || 0,
          hired: r.hired || 0,
          rating: r.rating || 3
        }));
        setRecruiterData(recruiters);
      }
    } catch (e) {
      console.error('Failed to load recruiter data:', e);
      toast.error('Failed to load recruiter data');
    }
  };

  // Load TAT data
  const loadTatData = async () => {
    try {
      const response = await jobDashboardApi.getTAT(dateRange.startDate, dateRange.endDate);
      if (response.success && response.data) {
        const tat = response.data.map(t => ({
          stage: t.stage || '',
          avgDays: t.avgDays || 0,
          count: t.count || 0,
          target: t.target || 0
        }));
        setTatData(tat);
      }
    } catch (e) {
      console.error('Failed to load TAT data:', e);
      toast.error('Failed to load TAT data');
    }
  };

  // Load source mix data
  const loadSourceData = async () => {
    try {
      const response = await jobDashboardApi.getSourceMix(dateRange.startDate, dateRange.endDate);
      if (response.success && response.data) {
        const sources = response.data.map((s, idx) => ({
          source: s.source || 'Unknown',
          count: s.count || 0,
          color: SOURCE_COLORS[idx % SOURCE_COLORS.length]
        }));
        setSourceData(sources);
      }
    } catch (e) {
      console.error('Failed to load source data:', e);
      toast.error('Failed to load source data');
    }
  };

  // Download handler
  const handleDownload = async (type) => {
    setIsDownloading(true);
    try {
      let response;
      let filename;
      const dateStr = `${dateRange.startDate}_to_${dateRange.endDate}`;

      switch (type) {
        case 'overview':
          response = await jobDashboardApi.downloadOverview(dateRange.startDate, dateRange.endDate);
          filename = `Job_Dashboard_Overview_${dateStr}.xlsx`;
          break;
        case 'segment':
          response = await jobDashboardApi.downloadSegments(dateRange.startDate, dateRange.endDate);
          filename = `Job_Dashboard_Segments_${dateStr}.xlsx`;
          break;
        case 'project':
          response = await jobDashboardApi.downloadProjects(dateRange.startDate, dateRange.endDate);
          filename = `Job_Dashboard_Projects_${dateStr}.xlsx`;
          break;
        case 'recruiter':
          response = await jobDashboardApi.downloadRecruiters(dateRange.startDate, dateRange.endDate);
          filename = `Job_Dashboard_Recruiters_${dateStr}.xlsx`;
          break;
        case 'tat':
          response = await jobDashboardApi.downloadTAT(dateRange.startDate, dateRange.endDate);
          filename = `Job_Dashboard_TAT_${dateStr}.xlsx`;
          break;
        case 'source':
          response = await jobDashboardApi.downloadSourceMix(dateRange.startDate, dateRange.endDate);
          filename = `Job_Dashboard_SourceMix_${dateStr}.xlsx`;
          break;
        default:
          throw new Error('Unknown download type');
      }

      // Create blob and download
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success(`Downloaded ${filename}`);
    } catch (e) {
      console.error('Download failed:', e);
      toast.error('Failed to download report');
    } finally {
      setIsDownloading(false);
    }
  };

  const loadAllData = useCallback(async () => {
    setIsLoading(true);
    try {
      await Promise.all([
        loadSummaryStats(),
        loadSegmentData(),
        loadProjectData(),
        loadRecruiterData(),
        loadTatData(),
        loadSourceData()
      ]);
    } finally {
      setIsLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  const getTatBadgeClass = (avgDays, target) => {
    const ratio = avgDays / target;
    if (ratio <= 0.6) return 'fast';
    if (ratio <= 0.9) return 'normal';
    if (ratio <= 1.1) return 'slow';
    return 'critical';
  };

  const renderStars = (rating) => {
    return (
      <div className="performance-stars">
        {[1, 2, 3, 4, 5].map(i => (
          i <= rating
            ? <MdStar key={i} />
            : <MdStarBorder key={i} className="star-empty" />
        ))}
      </div>
    );
  };

  const totalSourceCount = Array.isArray(sourceData) ? sourceData.reduce((sum, s) => sum + (s.count || 0), 0) : 0;

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <>
            {/* Summary Stats */}
            <div className="summary-stats-row">
              <div className="summary-stat-card">
                <div className="stat-icon purple"><MdWork /></div>
                <div className="stat-info">
                  <h4>Open JRs</h4>
                  <span className="stat-value">{summaryStats.totalOpenJRs}</span>
                </div>
              </div>
              <div className="summary-stat-card">
                <div className="stat-icon blue"><MdPeople /></div>
                <div className="stat-info">
                  <h4>Pipeline</h4>
                  <span className="stat-value">{summaryStats.totalCandidates}</span>
                </div>
              </div>
              <div className="summary-stat-card">
                <div className="stat-icon green"><MdCheckCircle /></div>
                <div className="stat-info">
                  <h4>Joined</h4>
                  <span className="stat-value">{summaryStats.joineesThisMonth}</span>
                </div>
              </div>
              <div className="summary-stat-card">
                <div className="stat-icon amber"><MdTrendingUp /></div>
                <div className="stat-info">
                  <h4>Fill Rate</h4>
                  <span className="stat-value">{summaryStats.fillRate}%</span>
                </div>
              </div>
            </div>

            <div className="dashboard-grid">
              {/* Segment Summary */}
              <div className="dashboard-card">
                <div className="card-header">
                  <h3><MdCategory className="header-icon" /> Segment Summary</h3>
                </div>
                <div className="card-body">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Segment</th>
                        <th>Open JRs</th>
                        <th>Pipeline</th>
                        <th>Joined</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(Array.isArray(segmentData) ? segmentData : []).slice(0, 5).map(seg => (
                        <tr key={seg.id}>
                          <td className="highlight">{seg.name}</td>
                          <td className="number-cell">{seg.openJRs}</td>
                          <td className="number-cell">{seg.pipeline}</td>
                          <td className="number-cell">{seg.joined}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Top Recruiters */}
              <div className="dashboard-card">
                <div className="card-header">
                  <h3><MdPerson className="header-icon" /> Top Recruiters</h3>
                </div>
                <div className="card-body">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Recruiter</th>
                        <th>Hired</th>
                        <th>Pipeline</th>
                        <th>Rating</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(Array.isArray(recruiterData) ? recruiterData : [])
                        .sort((a, b) => b.hired - a.hired)
                        .slice(0, 5)
                        .map(rec => (
                          <tr key={rec.id}>
                            <td className="highlight">{rec.name}</td>
                            <td className="number-cell">{rec.hired}</td>
                            <td className="number-cell">{rec.pipeline}</td>
                            <td>{renderStars(rec.rating)}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Source Mix */}
              <div className="dashboard-card">
                <div className="card-header">
                  <h3><MdPieChart className="header-icon" /> Source Mix</h3>
                </div>
                <div className="card-body">
                  <ul className="source-mix-list">
                    {(Array.isArray(sourceData) ? sourceData : []).slice(0, 5).map((src, idx) => (
                      <li key={idx} className="source-mix-item">
                        <div className="source-info">
                          <span className="source-color" style={{ background: src.color || SOURCE_COLORS[idx] }} />
                          <span className="source-name">{src.source}</span>
                        </div>
                        <div className="source-stats">
                          <span className="source-count">{src.count}</span>
                          <span className="source-percent">
                            {totalSourceCount > 0 ? Math.round((src.count / totalSourceCount) * 100) : 0}%
                          </span>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* TAT Summary */}
              <div className="dashboard-card">
                <div className="card-header">
                  <h3><MdAccessTime className="header-icon" /> TAT Summary</h3>
                </div>
                <div className="card-body">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Stage</th>
                        <th>Avg Days</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(Array.isArray(tatData) ? tatData : []).slice(0, 5).map((tat, idx) => (
                        <tr key={idx}>
                          <td>{tat.stage}</td>
                          <td className="number-cell">{tat.avgDays}</td>
                          <td>
                            <span className={`tat-badge ${getTatBadgeClass(tat.avgDays, tat.target)}`}>
                              {tat.avgDays <= tat.target ? 'On Track' : 'Delayed'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </>
        );

      case 'segment':
        return (
          <div className="dashboard-grid single-col">
            <div className="dashboard-card full-width">
              <div className="card-header">
                <h3><MdCategory className="header-icon" /> Segment-wise Analysis</h3>
              </div>
              <div className="card-body">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Segment/Vertical</th>
                      <th>Open JRs</th>
                      <th>Pipeline</th>
                      <th>Joined</th>
                      <th>Target</th>
                      <th>Progress</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(Array.isArray(segmentData) ? segmentData : []).map(seg => (
                      <tr key={seg.id}>
                        <td className="highlight">{seg.name}</td>
                        <td className="number-cell">{seg.openJRs}</td>
                        <td className="number-cell">{seg.pipeline}</td>
                        <td className="number-cell">{seg.joined}</td>
                        <td className="number-cell">{seg.target}</td>
                        <td>
                          <div className="progress-container">
                            <div className="progress-bar">
                              <div
                                className={`progress-fill ${seg.joined >= seg.target ? 'green' : 'blue'}`}
                                style={{ width: `${Math.min(100, (seg.joined / seg.target) * 100)}%` }}
                              />
                            </div>
                            <span className="progress-value">{Math.round((seg.joined / seg.target) * 100)}%</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {(!Array.isArray(segmentData) || segmentData.length === 0) && (
                  <div className="empty-state">
                    <h4>No segment data available</h4>
                    <p>Add verticals to see segment-wise analysis</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case 'project':
        return (
          <div className="dashboard-grid single-col">
            <div className="dashboard-card full-width">
              <div className="card-header">
                <h3><MdBusinessCenter className="header-icon" /> Project-wise Analysis</h3>
              </div>
              <div className="card-body">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Project/Client</th>
                      <th>Open JRs</th>
                      <th>Filled</th>
                      <th>Pending</th>
                      <th>Fill Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(Array.isArray(projectData) ? projectData : []).map(proj => (
                      <tr key={proj.id}>
                        <td className="highlight">{proj.name}</td>
                        <td className="number-cell">{proj.openJRs}</td>
                        <td className="number-cell">{proj.filled}</td>
                        <td className="number-cell">{proj.pending}</td>
                        <td>
                          <div className="progress-container">
                            <div className="progress-bar">
                              <div
                                className="progress-fill purple"
                                style={{ width: `${proj.openJRs > 0 ? (proj.filled / proj.openJRs) * 100 : 0}%` }}
                              />
                            </div>
                            <span className="progress-value">
                              {proj.openJRs > 0 ? Math.round((proj.filled / proj.openJRs) * 100) : 0}%
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {(!Array.isArray(projectData) || projectData.length === 0) && (
                  <div className="empty-state">
                    <h4>No project data available</h4>
                    <p>Add projects to see project-wise analysis</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case 'recruiter':
        return (
          <div className="dashboard-grid single-col">
            <div className="dashboard-card full-width">
              <div className="card-header">
                <h3><MdPerson className="header-icon" /> Recruiter Performance</h3>
              </div>
              <div className="card-body">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Recruiter</th>
                      <th>Role</th>
                      <th>Active JRs</th>
                      <th>Pipeline</th>
                      <th>Total Hired</th>
                      <th>Rating</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(Array.isArray(recruiterData) ? recruiterData : []).map(rec => (
                      <tr key={rec.id}>
                        <td className="highlight">{rec.name}</td>
                        <td>{rec.role?.replace(/_/g, ' ')}</td>
                        <td className="number-cell">{rec.activeJRs}</td>
                        <td className="number-cell">{rec.pipeline}</td>
                        <td className="number-cell">{rec.hired}</td>
                        <td>{renderStars(rec.rating)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {(!Array.isArray(recruiterData) || recruiterData.length === 0) && (
                  <div className="empty-state">
                    <h4>No recruiter data available</h4>
                    <p>Add recruiters to see performance analysis</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case 'tat':
        return (
          <div className="dashboard-grid single-col">
            <div className="dashboard-card full-width">
              <div className="card-header">
                <h3><MdAccessTime className="header-icon" /> TAT (Turn Around Time) Analysis</h3>
              </div>
              <div className="card-body">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Stage Transition</th>
                      <th>Avg Days</th>
                      <th>Target Days</th>
                      <th>Candidates</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(Array.isArray(tatData) ? tatData : []).map((tat, idx) => (
                      <tr key={idx}>
                        <td className="highlight">{tat.stage}</td>
                        <td className="number-cell">{tat.avgDays}</td>
                        <td className="number-cell">{tat.target}</td>
                        <td className="number-cell">{tat.count}</td>
                        <td>
                          <span className={`tat-badge ${getTatBadgeClass(tat.avgDays, tat.target)}`}>
                            {tat.avgDays <= tat.target * 0.6 && 'Fast'}
                            {tat.avgDays > tat.target * 0.6 && tat.avgDays <= tat.target && 'On Track'}
                            {tat.avgDays > tat.target && tat.avgDays <= tat.target * 1.2 && 'Slow'}
                            {tat.avgDays > tat.target * 1.2 && 'Critical'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );

      case 'source':
        return (
          <div className="dashboard-grid">
            <div className="dashboard-card full-width">
              <div className="card-header">
                <h3><MdPieChart className="header-icon" /> Source Mix Analysis</h3>
              </div>
              <div className="card-body">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Source</th>
                      <th>Candidates</th>
                      <th>Percentage</th>
                      <th>Distribution</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(Array.isArray(sourceData) ? sourceData : []).map((src, idx) => (
                      <tr key={idx}>
                        <td>
                          <div className="source-info">
                            <span className="source-color" style={{ background: src.color || SOURCE_COLORS[idx] }} />
                            <span className="highlight">{src.source}</span>
                          </div>
                        </td>
                        <td className="number-cell">{src.count}</td>
                        <td className="number-cell">
                          {totalSourceCount > 0 ? Math.round((src.count / totalSourceCount) * 100) : 0}%
                        </td>
                        <td>
                          <div className="progress-container">
                            <div className="progress-bar">
                              <div
                                className="progress-fill"
                                style={{
                                  width: `${totalSourceCount > 0 ? (src.count / totalSourceCount) * 100 : 0}%`,
                                  background: src.color || SOURCE_COLORS[idx]
                                }}
                              />
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="admin-view job-dashboard-view">
      {/* Compact Header with Filters - At Top */}
      <div className="page-header-compact">
        <div className="header-left-compact">
          <h2 className="page-title-compact">
            <MdDashboard className="title-icon" />
            Job Dashboard
          </h2>
        </div>
        <div className="header-filters-compact">
          <input
            type="date"
            className="filter-select-compact"
            value={dateRange.startDate}
            onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
          />
          <input
            type="date"
            className="filter-select-compact"
            value={dateRange.endDate}
            onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
          />
          <button className="btn-book-compact" onClick={loadAllData} disabled={isLoading}>
            <MdRefresh />
          </button>
          <button
            className="btn-book-compact btn-download"
            onClick={() => handleDownload(activeTab)}
            disabled={isDownloading}
          >
            <MdFileDownload />
            <span>{isDownloading ? 'Exporting...' : 'Export'}</span>
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="dashboard-tabs">
        {DASHBOARD_TABS.map(tab => (
          <button
            key={tab.id}
            className={`dashboard-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <tab.icon className="tab-icon" />
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="loading-state">Loading dashboard data...</div>
      ) : (
        renderTabContent()
      )}
    </div>
  );
}

export default JobDashboard;
